import LFCButton from '_components/inputs/LFCButton';
import LFCTextField from '_components/inputs/LFCTextField';
import {DeviceInfo, upsertDeviceInfo} from '_logics/LFCDeviceInfoUtil';
import {useSnackbar} from 'notistack';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Dialog, DialogActions, DialogContent, DialogTitle, Grid} from '@mui/material';

type DeviceInfoDialogProps = {
  open: boolean;
  row: DeviceInfo & {id: string};
  onClose: () => void;
  onSaved: () => void;
};

const DeviceInfoDialog = ({open, row, onClose, onSaved}: DeviceInfoDialogProps) => {
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    display_name: row.display_name ?? '',
    robot_manufacturer: row.robot_manufacturer ?? '',
    robot_model: row.robot_model ?? '',
    installation_location: row.installation_location ?? '',
    department: row.department ?? '',
    manager: row.manager ?? '',
    memo: row.memo ?? ''
  });

  const handleChange =
    (field: keyof typeof form) =>
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement> | undefined) => {
      setForm(prev => ({...prev, [field]: event?.target.value ?? ''}));
    };

  const handleSave = () => {
    setSaving(true);
    upsertDeviceInfo({
      snack: enqueueSnackbar,
      t,
      info: {
        device_name: row.device_name,
        ...Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v === '' ? null : v]))
      }
    })
      .then(() => onSaved())
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  const fields: [keyof typeof form, string][] = [
    ['display_name', '表示名'],
    ['robot_manufacturer', 'ロボットメーカー'],
    ['robot_model', '機種'],
    ['installation_location', '設置場所'],
    ['department', '管理部署'],
    ['manager', '管理担当者']
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('端末情報を編集')} — {row.device_name}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{mt: 0.5}}>
          {fields.map(([field, label]) => (
            <Grid item xs={12} sm={6} key={field}>
              <LFCTextField
                name={field}
                label={t(label)}
                value={form[field]}
                onChange={handleChange(field)}
                fullWidth
              />
            </Grid>
          ))}
          <Grid item xs={12}>
            <LFCTextField
              name="memo"
              label={t('メモ')}
              value={form.memo}
              onChange={handleChange('memo')}
              fullWidth
              multiline
              rows={3}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <LFCButton onClick={onClose} disabled={saving}>
          {t('キャンセル')}
        </LFCButton>
        <LFCButton onClick={handleSave} disabled={saving} color="primary">
          {t('保存')}
        </LFCButton>
      </DialogActions>
    </Dialog>
  );
};

export default DeviceInfoDialog;
